import type { ComputedRef, Ref } from 'vue'
import type { AppSelectOption } from '@brighterly/lib-core-types/ui/selects/interfaces'
import type { Promocode } from './interfaces'

import { DateFormat } from '@brighterly/lib-core-types/date/enums'

import { useDayjs } from '@shared/lib/format'

import { usePromocodesStore } from './store'
import { usePromocodesApi } from '../api/list.hook'
import { usePromocodeUpdate } from '../api/update.hook'
import { usePromocodeOptions } from './promocode-options'

interface EditPromocodeReturn {
  isOpen: ComputedRef<boolean>
  promocode: ComputedRef<Promocode | null>
  refStatus: Ref<string>
  refExpiresAt: Ref<string>
  statusOptions: ComputedRef<AppSelectOption[]>
  isSaving: ComputedRef<boolean>
  isSaveDisabled: ComputedRef<boolean>
  save: () => Promise<void>
  cancel: () => void
}

export const useEditPromocode = (): EditPromocodeReturn => {
  const store = usePromocodesStore()
  const dayjs = useDayjs()
  const { statusOptions } = usePromocodeOptions()

  const isOpen = computed<boolean>(() => store.isModalEdit)
  const promocode = computed<Promocode | null>(() => store.getPromocodeToEdit)

  const refStatus = ref<string>('')
  const refExpiresAt = ref<string>('')

  const isSaving = computed<boolean>(() => store.isSaving)

  const original = ref<string>('')
  const snapshot = (): string => JSON.stringify({ status: refStatus.value, expiresAt: refExpiresAt.value })
  const isSaveDisabled = computed<boolean>(() => !refStatus.value || snapshot() === original.value)

  const apply = (loaded: Promocode): void => {
    refStatus.value = loaded.status
    refExpiresAt.value = loaded.expires_at ? dayjs(loaded.expires_at).format(DateFormat.DefaultDate) : ''
    original.value = snapshot()
  }

  const cancel = (): void => {
    store.setPromocodeToEdit(null)
  }

  const save = async (): Promise<void> => {
    const target = store.getPromocodeToEdit

    if (!target) return

    const updated = await usePromocodeUpdate({
      promocodeId: target.id,
      payload: {
        status: refStatus.value,
        expires_at: refExpiresAt.value ? dayjs(refExpiresAt.value).endOf('day').format(DateFormat.Default) : null,
      },
    })

    if (updated) {
      store.setPromocodeToEdit(null)
      await usePromocodesApi()
    }
  }

  watch(promocode, (target) => {
    if (target) apply(target)
  })

  return {
    isOpen,
    promocode,
    refStatus,
    refExpiresAt,
    statusOptions,
    isSaving,
    isSaveDisabled,
    save,
    cancel,
  }
}
